<template>
  <v-app>
    <NavBar />
    <FlashMessage />
    <v-dialog
      max-width="460"
      persistent
      v-model="showAddressUpdateWindow"
    >
      <div class="address-update">
        <h6 class="address-update--title">Action Needed: Update Address</h6>
        <p class="address-update--text">Please review and update your address details by clicking the button below.</p>
        <v-btn class="address-update--btn" color="#1976D2" @click="handleAddressUpdate">Update Address</v-btn>
      </div>
    </v-dialog>
    <v-dialog
      max-width="640"
      persistent
      v-model="showWorkloadUpdateWindow"
    >
      <div class="address-update">
        <h6 class="address-update--title">Action Needed: Update Workload</h6>
        <p class="address-update--text">Please take a moment to review and adjust your workload settings to reflect your typical daily capacity. These values are used to calculate your availability and task distribution.</p>
        <div class="address-update--container">
          <v-btn class="address-update--btnSplit" text @click="handleWorkloadAlreadyUpdated">Already updated</v-btn>
          <v-btn class="address-update--btnSplit address-update--btnSplitPrimary" color="#1976D2" @click="handleWorkloadSettingsReview">Review Your Workload Settings</v-btn>
        </div>
      </div>
    </v-dialog>
    <v-main
      :class="{ 
        'grey lighten-4': $route.name !== 'Reimbursement Request Form' 
          || $route.name !== 'Request an Assignment'
          || $route.name !== 'Vendor Invoice Form',
        'grey darken-2': $route.name === 'Reimbursement Request Form' 
          || $route.name === 'Request an Assignment'
          || $route.name === 'Vendor Invoice Form'
        }">
      <router-view />
    </v-main>
  </v-app>
</template>

<script>
// vuex
import { mapActions, mapGetters } from "vuex";
// internal
import "@/styles/styles.scss";
import { validateToken, refreshSession } from "@/utils/axiosInstance";
// components
import NavBar from "@/components/ui/NavBar";
import FlashMessage from "@/components/ui/FlashMessage";

export default {
  name: "App",
  components: { NavBar, FlashMessage },
  data: () => ({
    showWorkloadUpdateWindow: false,
    workloadUpdateWindowStorageKey: "workloadUpdateWindowSeen",
  }),
  computed: {
    ...mapGetters("auth", ["user", "profileModal", "showAddressUpdateWindow", "showAIToolsUpdateWindow"]),
  },
  watch: {
    profileModal() {
      if (!this.profileModal) {
        const token = localStorage.getItem("token");
        validateToken(token).then((validResp) => {
          if (validResp.status === 200) {
            this.setUser(validResp.data);
            this.syncActionNeededModals();
          }
        });
      }
    }
  },
  methods: {
    ...mapActions("auth", [
      "setIsAdmin",
      "setIsManager",
      "setUser",
      "setIsDubEditor",
      "handleProfileModal",
      "setProfileModalTab",
      "setProfileModalIsViewMode",
      "setShowAddressUpdateWindow",
      "setShowAIToolsUpdateWindow"
    ]),
    ...mapActions("flashMessage", ["handleFlash"]),
    isStaffNonVendor() {
      return (
        this.user?.user?.userRole !== "CLIENT" &&
        !this.user?.adminDetails?.vendor
      );
    },
    shouldShowAddressModal() {
      return (
        !this.user?.hrDetails?.streetAddress ||
        !this.user?.hrDetails?.city ||
        !this.user?.hrDetails?.zipCode
      );
    },
    shouldShowWorkloadModal() {
      return (
        localStorage.getItem(this.workloadUpdateWindowStorageKey) !== "true"
      );
    },
    syncActionNeededModals() {
      if (!this.isStaffNonVendor()) {
        this.setShowAddressUpdateWindow(false);
        this.showWorkloadUpdateWindow = false;
        return;
      }

      const showAddressModal = this.shouldShowAddressModal();
      this.setShowAddressUpdateWindow(showAddressModal);
      this.showWorkloadUpdateWindow =
        !showAddressModal && this.shouldShowWorkloadModal();
    },
    handleAddressUpdate() {
      this.setProfileModalTab(2);
      this.setProfileModalIsViewMode(false);
      this.handleProfileModal(true);
      this.setShowAddressUpdateWindow(false);
    },
    handleWorkloadSettingsReview() {
      localStorage.setItem(this.workloadUpdateWindowStorageKey, "true");
      this.showWorkloadUpdateWindow = false;
      this.$router.push({
        name: "Availability Calendar",
        query: { openDefaultCapacity: "true" },
      });
    },
    handleWorkloadAlreadyUpdated() {
      localStorage.setItem(this.workloadUpdateWindowStorageKey, "true");
      this.showWorkloadUpdateWindow = false;
    },
    dropSession() {
      this.setUser(null);
      this.setIsAdmin(false);
      this.setIsManager(false);
      this.setIsDubEditor(false);
      this.setShowAddressUpdateWindow(false);
      this.showWorkloadUpdateWindow = false;
    },
  },
  mounted() {
    const token = localStorage.getItem("token");
    const refreshToken = localStorage.getItem("refreshToken");
    const handleAuth = (auth) => {
      this.setIsAdmin(auth.data.user.userRole === "ADMIN");
      this.setIsManager(auth.data.user.userRole === "MANAGER");
      this.setIsDubEditor(!!auth.data?.adminDetails?.dubEditorAccess);
      this.setUser(auth.data);
      this.syncActionNeededModals();
      if (this.$route.path === "/login") {
        if (this.$route?.query?.to) this.$router.push(this.$route.query.to);
        else this.$router.push("/");
      }
    };
    if (!token) {
      this.dropSession();
      if (this.$route.path !== "/login") this.$router.push("/login");
    } else {
      validateToken(token).then((validResp) => {
        if (validResp.status === 200) {
          handleAuth(validResp);
        } else {
          refreshSession(refreshToken).then((refResp) => {
            if (refResp.status === 200) {
              localStorage.setItem("token", refResp.data.accessToken);
              validateToken(refResp.data.accessToken).then((refResp) => {
                handleAuth(refResp);
              });
            } else {
              this.dropSession();
              if (this.$route.path !== "/login") this.$router.push("/login");
              this.handleFlash({ response: refResp, show: true });
            }
          });
        }
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.address-update {
  width: 100%;
  background-color: #FFF;
  padding: 15px;
  &--title {
    font-family: Roboto;
    font-size: 20px;
    font-weight: 500;
    line-height: 18px;
    text-align: left;
  }
  &--text {
    font-family: Roboto;
    font-size: 14px;
    font-weight: 300;
    line-height: 18px;
    text-align: left;
    margin-top: 30px;
    margin-bottom: 0;
  }
  &--container {
    margin-top: 35px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
  &--btn {
    width: 100%;
    margin-top: 35px;
    color: #FFF !important;
  }
  &--btnSplit {
    margin-top: 35px;
    flex: 1;
  }
  &--btnSplitPrimary {
    color: #FFF !important;
  }
  &--btnTools {
    color: #FFF !important;
  }
}
</style>
